import React, { useMemo } from 'react'
import _ from 'lodash'

import TextGroup, { WorkTypes } from '../Texts'
import { TOOL_CATEGORY_ORDER, TOOLS } from '../../constants/toolsCatalog'

import tileFrontend from '../../assets/images/tiles/Frontend.svg'
import tileBackend from '../../assets/images/tiles/Backend.svg'
import tileDatabases from '../../assets/images/tiles/Databases.svg'
import tileSoftware from '../../assets/images/tiles/Software-Development.svg'
import tileTechAudits from '../../assets/images/tiles/Tech-Audits.svg'

const CATEGORY_SECTIONS = {
  ai: {
    id: 'ai-ml',
    tile: tileSoftware,
    title: { en: 'AI & ML', pt: 'IA & ML' },
    description: {
      en: 'We design and ship intelligent products with production-ready AI stacks — from model APIs and vector stores to orchestration, evaluation, and MLOps workflows that scale with your product.',
      pt: 'Concebemos e entregamos produtos inteligentes com stacks de IA prontos para produção — desde APIs de modelos e vector stores até orquestração, avaliação e fluxos MLOps que escalam com o seu produto.',
    },
  },
  frontend: {
    id: 'frontend-architecture',
    tile: tileFrontend,
    title: { en: 'Front-End Architecture', pt: 'Arquitetura de Front-End' },
    description: {
      en: 'We improve user experience through various technologies and frameworks founded in server-side rendering and caching systems, allowing for fast content display and minimal resource consumption.',
      pt: 'Aprimoramos a experiência do utilizador através de diversas tecnologias e frameworks, com alicerces em server-side rendering e sistemas de cache que permitem uma apresentação rápida de conteúdos com consumos mínimos de recursos.',
    },
  },
  backend: {
    id: 'backend-architecture',
    tile: tileBackend,
    title: { en: 'Back-End Architecture', pt: 'Arquitetura de Back-End' },
    description: {
      en: 'We believe in administrative tools that are as intuitive as they are flexible, and as such, one of our goals is to provide each client with a tailor-made, expandable back-end.',
      pt: 'Acreditamos em ferramentas administrativas que sejam tão intuitivas como flexíveis, e como tal, um dos nossos objetivos é proporcionar a cada cliente um back-end expansível e feito à medida.',
    },
  },
  data: {
    id: 'data-analytics',
    tile: tileDatabases,
    title: { en: 'Data & Analytics', pt: 'Dados & Analytics' },
    description: {
      en: 'We build data platforms that turn raw information into reliable insights — combining warehousing, streaming, transformation, and BI tools to support reporting, analytics, and AI-ready datasets.',
      pt: 'Construímos plataformas de dados que transformam informação bruta em insights fiáveis — combinando warehousing, streaming, transformação e ferramentas de BI para suportar reporting, analytics e datasets prontos para IA.',
    },
  },
  database: {
    id: 'databases',
    tile: tileDatabases,
    title: { en: 'Databases', pt: 'Bases de Dados' },
    description: {
      en: 'Using relevant and renowned technologies, we pick the database model that best suits your business or objective, from editing institutional contents to managing products, services, and sales.',
      pt: 'Utilizando tecnologias relevantes e de renome, escolhemos o modelo de base de dados que melhor se adequa ao seu negócio ou objetivo, desde a edição de conteúdos institucionais à gestão de produtos, serviços e vendas.',
    },
  },
  cloud: {
    id: 'cloud',
    tile: tileBackend,
    title: { en: 'Cloud', pt: 'Cloud' },
    description: {
      en: 'Our team advocates the use of cloud computing for ease of access and a smoother update process across the board — selecting the right hyperscaler and managed services for each workload.',
      pt: 'A nossa equipa defende o uso de tecnologias em cloud para um acesso mais fácil e um processo de atualização uniformizado — selecionando o hyperscaler e os serviços geridos certos para cada workload.',
    },
  },
  devops: {
    id: 'devops',
    tile: tileBackend,
    title: { en: 'DevOps', pt: 'DevOps' },
    description: {
      en: 'We employ Continuous Integration and Continuous Delivery methods, mitigating the risk of an outdated code base and potential conflicts, as well as delivering software updates in shorter cycles with observable, automated infrastructure.',
      pt: 'Utilizamos métodos de Continuous Integration e Continuous Delivery, mitigando o risco de uma base de código desatualizada e potenciais conflitos, assim como a entrega de atualizações de software em ciclos mais curtos com infraestrutura observável e automatizada.',
    },
  },
  mobile: {
    id: 'mobile',
    tile: tileSoftware,
    title: { en: 'Mobile', pt: 'Mobile' },
    description: {
      en: 'We deliver native and cross-platform mobile experiences with modern frameworks and languages — balancing performance, maintainability, and a consistent user experience across iOS and Android.',
      pt: 'Entregamos experiências mobile nativas e cross-platform com frameworks e linguagens modernas — equilibrando performance, manutenibilidade e uma experiência de utilizador consistente em iOS e Android.',
    },
  },
  security: {
    id: 'security',
    tile: tileTechAudits,
    title: { en: 'Security', pt: 'Segurança' },
    description: {
      en: 'Security is embedded across identity, secrets, application scanning, and runtime protection — using proven tools to reduce risk without slowing delivery.',
      pt: 'A segurança está integrada em identidade, secrets, análise de aplicações e proteção em runtime — utilizando ferramentas comprovadas para reduzir risco sem abrandar a entrega.',
    },
  },
  messageBroker: {
    id: 'message-brokers',
    tile: tileDatabases,
    title: { en: 'Messaging', pt: 'Messaging' },
    description: {
      en: 'Different needs require different platforms, and when intercommunication is a must, we facilitate it via message brokers, preserving data integrity on both ends through reliable, tried and true systems.',
      pt: 'Cada plataforma traz consigo diferentes necessidades, e quando a intercomunicação é indispensável, facilitamo-la com message brokers, preservando a integridade dos dados em ambas as extremidades através de sistemas fidedignos e comprovados.',
    },
  },
}

const toolsByCategory = TOOL_CATEGORY_ORDER.reduce((acc, key) => {
  acc[key] = TOOLS.filter(tool => tool.category === key).sort((a, b) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
  )
  return acc
}, {})

const ToolboxTwo = ({ lang }) => {
  const isEn = lang === 'en'

  const toolBlocks = useMemo(
    () =>
      TOOL_CATEGORY_ORDER.map(categoryKey => {
        const section = CATEGORY_SECTIONS[categoryKey]
        const tools = toolsByCategory[categoryKey] ?? []

        return {
          rootProps: { name: section.id, className: 'row', id: section.id },
          textLeadImage: section.tile,
          text: {
            rootProps: { className: 'col-6 col-12-medium' },
            title: {
              type: 'h2',
              text: isEn ? section.title.en : section.title.pt,
            },
            description: {
              text: isEn ? section.description.en : section.description.pt,
            },
          },
          technologies: {
            rootProps: { className: 'col-6 col-12-medium' },
            items: [
              {
                logos: tools.map(tool => ({
                  src: tool.image,
                  title: tool.title,
                })),
              },
            ],
          },
        }
      }),
    [isEn]
  )

  return (
    <section id="toolbox-two" className="container content">
      <div className="tools">
        {_.map(toolBlocks, tool => (
          <div {...tool.rootProps} key={tool.rootProps.id}>
            <TextGroup {...tool.text}>
              <div className="image">
                <img src={tool.textLeadImage} alt="" />
              </div>
            </TextGroup>
            <div {...tool.technologies.rootProps}>
              {_.map(tool.technologies.items, (item, i) => (
                <div className="toolbox-group" key={`toolbox-${tool.rootProps.id}-${i}`}>
                  <WorkTypes items={item.toolType} />
                  <div className="logos">
                    {_.map(item.logos, logo => (
                      <div className="logo" key={logo.title}>
                        <img
                          src={logo.src}
                          title={logo.title}
                          alt={logo.title}
                          width="150px"
                          height="100px"
                          loading="lazy"
                        />
                        {logo.title}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default ToolboxTwo
